import React from 'react';
import { Bell, Search, LogOut, Bug } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

const Topbar = ({ title }) => {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const [showNotifications, setShowNotifications] = React.useState(false);
  
  if (!user) return null;

  const initials = user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const roleLabel = user.role.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <div className="topbar" style={{
      background: 'var(--color-background-primary)',
      borderBottom: '1px solid var(--color-border-tertiary)',
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '64px',
      position: 'relative'
    }}>
      <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
        {title || 'Dashboard'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ 
            position: 'absolute', 
            left: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            color: 'var(--color-text-tertiary)' 
          }} />
          <input 
            type="text" 
            placeholder="Search..." 
            style={{
              padding: '8px 12px 8px 36px',
              borderRadius: 'var(--border-radius-md)',
              border: '1px solid var(--color-border-tertiary)',
              background: 'var(--color-background-secondary)',
              fontSize: '13px',
              width: '240px'
            }}
          />
        </div>

        <div 
          style={{ position: 'relative', cursor: 'pointer' }}
          onClick={() => setShowNotifications(!showNotifications)}
        >
          <Bell size={20} color="var(--color-text-secondary)" />
          {unreadCount > 0 && (
            <div style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              background: '#E24B4A',
              color: '#fff',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              fontSize: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              border: '2px solid white'
            }}>
              {unreadCount}
            </div>
          )}

          {showNotifications && (
            <div style={{
              position: 'absolute',
              top: '40px',
              right: '0',
              width: '300px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              border: '1px solid var(--color-border-tertiary)',
              zIndex: 100,
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '700', fontSize: '14px' }}>Notifications</span>
                {unreadCount > 0 && <span style={{ fontSize: '11px', color: 'var(--color-brand-primary)', fontWeight: '600' }}>{unreadCount} New</span>}
              </div>
              {notifications.length > 0 ? (
                notifications.map(notification => (
                  <div 
                    key={notification._id} 
                    onClick={() => markAsRead(notification._id)}
                    style={{ 
                      padding: '12px 16px', 
                      borderBottom: '1px solid var(--color-border-tertiary)',
                      background: notification.isRead ? 'white' : '#f0f7ff',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start'
                    }}
                  >
                    {!notification.isRead && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#E24B4A',
                        marginTop: '5px',
                        flexShrink: 0
                      }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: notification.isRead ? '500' : '700', marginBottom: '2px' }}>{notification.title}</div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: '1.4' }}>{notification.message}</div>
                      <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>{new Date(notification.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: '13px' }}>
                  No notifications yet
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', borderLeft: '1px solid var(--color-border-tertiary)', paddingLeft: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>{user.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{roleLabel}</div>
            </div>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'var(--color-background-info)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'var(--color-brand-secondary)'
            }}>
              {initials}
            </div>
          </div>
          
          <button 
            onClick={() => window.location.href='/bug-report'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid var(--color-border-primary)',
              background: 'white',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            <Bug size={14} /> Report Bug
          </button>

          <button 
            onClick={logout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid var(--color-border-primary)',
              background: 'white',
              fontSize: '12px',
              color: '#ef4444',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Topbar;
